// react
import styled, { css } from 'styled-components';
import { useMemo, useState } from 'react';
import { theme } from '@/theme';

// atoms
import { Button } from '@/components/atoms/Button/Button';
import { Divider } from '@/components/atoms/Divider/Divider';
import { Title } from '@/components/atoms/Title/Title';
import { Icon } from '@/components/atoms/Icon/Icon';
import { Dialog } from '@/components/atoms/Dialog/Dialog';

// organisms
import { Table } from '@/components/organisms/Table/Table';

// models
import { CareRecipientSummary } from '@karehero/models';
import SimpleTable from '@/components/molecules/SimpleTable/SimpleTable';

interface PortalClientSummaryProps {
  recipient: CareRecipientSummary;
  isMobile?: boolean;
  onPrintPdf: () => void;
}

/**
 * The PortalClientSummary component displays a summary of a care recipient's
 * care profile and shortlist and financial assessment.
 */
export const PortalClientSummary = ({
  recipient,
  isMobile,
  onPrintPdf,
}: PortalClientSummaryProps) => {
  // states
  const [isFinancialSummaryDialogOpen, setIsFinancialSummaryDialogOpen] =
    useState(false);

  // memos
  const shortlistData = useMemo(
    () => recipient?.careRecipientShortlistData || [],
    [recipient],
  );

  const shortlistUrl = useMemo(
    () =>
      recipient?.careRecipientShortlistData
        ? `https://caresourcer.com/uk/shortlist/${recipient.careRecipientShortlistData[0]?.shortlistID}`
        : null,
    [recipient],
  );

  const careRecipientAssessmentDate = useMemo(() => {
    return new Date(recipient.careRecipientAssessmentDate).toLocaleDateString(
      'en-GB',
    );
  }, [recipient.careRecipientAssessmentDate]);

  const careRecipientFinancialAssessmentDate = useMemo(() => {
    return new Date(
      recipient.careRecipientFinancialAssessmentDate,
    ).toLocaleDateString('en-GB');
  }, [recipient.careRecipientFinancialAssessmentDate]);

  return (
    <>
      <CareRecipientHeading>
        <CareRecipientLabel>Care recipient: </CareRecipientLabel>
        <CareRecipientName>
          {recipient?.fullName || 'Unknown'}
        </CareRecipientName>
      </CareRecipientHeading>
      <Divider isOr={false} margin={16} />

      <Summary $isMobile={isMobile}>
        {/* Care Assessment */}
        <div>
          <SummaryItemHeading $isMobile={isMobile}>
            <SummaryItemText $isMobile={isMobile}>
              <Title size='sm' isNoMargin={isMobile}>
                Care Assessment
              </Title>{' '}
              {!isMobile && ' | '}
              {recipient.careRecipientAssessmentStatus ? (
                recipient.careRecipientAssessmentStatus.toLowerCase() ===
                'complete' ? (
                  <div>Complete {careRecipientAssessmentDate}</div>
                ) : (
                  <div>{recipient.careRecipientAssessmentStatus}</div>
                )
              ) : (
                <div>Not Started</div>
              )}
            </SummaryItemText>
            {!isMobile && shortlistUrl && (
              <div className='summary-item-actions'>
                <Button
                  size='sm'
                  ariaLabel='view shortlist'
                  variant='secondary'
                  to={shortlistUrl}
                  target='_blank'
                >
                  View Shortlist
                </Button>
              </div>
            )}
          </SummaryItemHeading>
          <SummaryItemSubtitle>
            KareHero have recommended the following care providers to support{' '}
            {recipient.firstName}
            's needs:
          </SummaryItemSubtitle>
          <Table
            actions={[]}
            isPaginated={false}
            isSearchable={false}
            isTotalRows={false}
            isBorderBottom={true}
            columns={[
              {
                id: 'name',
                header: 'Care Provider',
                accessorKey: 'name',
              },
              {
                id: 'status',
                header: 'Status',
                accessorKey: 'status',
                cell: (info) => (
                  <StatusCell>
                    <span>{info.getValue()}</span>
                    {info.getValue() === 'Secured' && (
                      <Icon
                        icon='check'
                        type='fas'
                        size='2xs'
                        fill={theme.color.success[40]}
                      />
                    )}
                  </StatusCell>
                ),
              },
            ]}
            rows={shortlistData[0]?.items?.map((item) => ({
              name: item.name,
              status:
                item.regulatoryID == recipient.securedRegulatoryID
                  ? 'Secured'
                  : 'Shortlisted',
            }))}
          />
          {isMobile && shortlistUrl && (
            <div className='summary-item-actions'>
              <br />

              <MobileButton
                size='sm'
                ariaLabel='view shortlist'
                variant='secondary'
                to={shortlistUrl}
                target='_blank'
              >
                View shortlist
              </MobileButton>
              <Divider isOr={false} margin={16} />
            </div>
          )}
        </div>
        {/* Financial Assessment */}
        <div className='summary-item'>
          <SummaryItemHeading $isMobile={isMobile}>
            <SummaryItemText>
              <Title size='sm' isNoMargin={isMobile}>
                Financial Assessment
              </Title>
              {!isMobile && ' | '}
              <div>
                {recipient.careRecipientFinancialAssessmentStatus ? (
                  recipient.careRecipientFinancialAssessmentStatus.toLowerCase() ===
                  'complete' ? (
                    <div>Complete {careRecipientFinancialAssessmentDate}</div>
                  ) : (
                    <div>
                      {recipient.careRecipientFinancialAssessmentStatus}
                    </div>
                  )
                ) : (
                  <div>Not started</div>
                )}
              </div>
            </SummaryItemText>
            {recipient.careRecipientFinancialAssessmentDate && (
              <div className='summary-item-actions'>
                <Button
                  size='sm'
                  ariaLabel='view financial summary'
                  variant='secondary'
                  onPress={() => setIsFinancialSummaryDialogOpen(true)}
                >
                  View Financial Summary
                </Button>
              </div>
            )}
          </SummaryItemHeading>
        </div>
      </Summary>

      <Dialog
        title='Client Financial Summary'
        isCloseButton
        isOpen={isFinancialSummaryDialogOpen}
        setIsOpen={(isOpen: boolean) => setIsFinancialSummaryDialogOpen(isOpen)}
        variant='dialog'
        minSize='lg'
      >
        <SimpleTable
          rows={recipient.careRecipientFinancialAssessmentAnswers}
          actions={[
            {
              label: 'Print',
              onPress: onPrintPdf,
            },
          ]}
        />
      </Dialog>
    </>
  );
};

// Care Recipient
const CareRecipientHeading = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: ${(props) => props.theme.spacing[48]};
`;

const CareRecipientLabel = styled.div`
  font: ${(props) => props.theme.typography.heading['2xl']};
  color: ${(props) => props.theme.color.primary[80]};
`;

const CareRecipientName = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[90]};
`;

const Summary = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${(props) => !props.$isMobile && props.theme.spacing[80]};
`;

const SummaryItemHeading = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  align-items: baseline;
  gap: ${(props) => (props.$isMobile ? '16px' : '8px')};

  justify-content: space-between;

  ${(props) =>
    props.$isMobile &&
    css`
      margin-top: ${(props) => props.theme.spacing[16]};
      margin-bottom: ${(props) => props.theme.spacing[16]};
    `}
`;

const SummaryItemSubtitle = styled.div`
  font: ${(props) => props.theme.typography.body.lg.medium};
  color: ${(props) => props.theme.color.primary[80]};
  margin-bottom: ${(props) => props.theme.spacing[16]};
`;

const SummaryItemText = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  align-items: baseline;
  gap: 8px;

  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[90]};

  ${(props) =>
    props.$isMobile &&
    css`
      flex-direction: column;
      align-items: flex-start;
    `}
`;

const MobileButton = styled(Button)`
  width: 100%;
`;

const StatusCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[8]};
`;

PortalClientSummary.displayName = 'PortalClientSummary';

export default PortalClientSummary;
